/* Animated monsters for while they're working: every <span class="monster" data-monster="..."> the app renders gets its Lottie animation,
   Fiona's for the standard reader, Grogg Alex's for Kraken, and Winnie's on the sign-in page. The app builds its markup with innerHTML, so a
   MutationObserver mounts animations as elements appear and destroys them once they leave the page.
   Reduced motion holds each one on its first frame; if an animation can't load, the static drawing is shown instead. */
import lottie from "lottie-web/build/player/lottie_light";

type AnimationItem = ReturnType<typeof lottie.loadAnimation>;
// viewBox crops the animation to the part the character moves in; keep it in step with that monster's aspect-ratio in styles.css.
export type MonsterArt = { anim: string; still: string; viewBox?: string };
// Fiona's and Kraken's animations are 1080x1080 with empty space above and below the character.
const VIEW_BOX = "40 100 1000 880";

export function mountMonsters(art: Record<string, MonsterArt>, root: HTMLElement = document.body): void {
  const still = matchMedia("(prefers-reduced-motion: reduce)");
  const data = new Map<string, Promise<unknown>>();
  const live = new Map<HTMLElement, AnimationItem>();

  const load = (key: string, url: string): Promise<unknown> => {
    let p = data.get(key);
    if (!p) {
      p = fetch(url).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() as Promise<unknown>; });
      p.catch(() => data.delete(key)); // let a later render try again
      data.set(key, p);
    }
    return p;
  };

  const fallback = (el: HTMLElement, src: string): void => {
    const img = document.createElement("img"); img.src = src; img.alt = ""; img.draggable = false;
    el.replaceChildren(img); el.classList.add("still");
  };

  const mount = async (el: HTMLElement): Promise<void> => {
    if (el.dataset.mounted) return;
    el.dataset.mounted = "1";
    const key = el.dataset.monster ?? "", a = art[key];
    if (!a) return;
    let json: unknown;
    try { json = await load(key, a.anim); } catch { fallback(el, a.still); return; }
    if (!el.isConnected) return;
    // lottie writes into the data it's given, so each instance gets its own copy
    const anim = lottie.loadAnimation({ container: el, renderer: "svg", loop: true, autoplay: !still.matches, animationData: structuredClone(json),
      rendererSettings: { viewBoxSize: a.viewBox ?? VIEW_BOX } });
    live.set(el, anim);
  };

  const scan = (): void => {
    for (const [el, anim] of live) if (!el.isConnected) { anim.destroy(); live.delete(el); }
    root.querySelectorAll<HTMLElement>(".monster[data-monster]:not([data-mounted])").forEach(el => void mount(el));
  };

  new MutationObserver(scan).observe(root, { childList: true, subtree: true });
  still.addEventListener("change", () => { for (const anim of live.values()) still.matches ? anim.goToAndStop(0, true) : anim.play(); });
  scan();
}
